if (!customElements.get('delivery-estimate')) {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const ZIP_STORAGE_KEY = 'deliveryEstimate:zip';

  // Dates below are shop-local wall-clock times stored in a Date's UTC fields,
  // so getUTCDay()/getUTCHours() read as the shop's day and hour.
  const DeliveryEstimateUtils = {
    parseOffset(offset) {
      const match = /^([+-])(\d{2}):?(\d{2})$/.exec(offset || '');
      if (!match) return 0;
      const minutes = parseInt(match[2], 10) * 60 + parseInt(match[3], 10);
      return (match[1] === '-' ? -1 : 1) * minutes;
    },

    shopNow(offset, now = Date.now()) {
      return new Date(now + DeliveryEstimateUtils.parseOffset(offset) * 60 * 1000);
    },

    isoDate(date) {
      return date.toISOString().slice(0, 10);
    },

    startOfDay(date) {
      return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    },

    parseHolidays(text) {
      return new Set(
        (text || '')
          .split(/[\n,]/)
          .map((line) => line.trim())
          .filter((line) => /^\d{4}-\d{2}-\d{2}$/.test(line))
      );
    },

    parseZones(text) {
      return (text || '')
        .split('\n')
        .map((line) => line.split('|').map((part) => part.trim()))
        .filter((parts) => parts.length >= 3)
        .map(([label, prefixes, days]) => {
          const range = /^(\d+)\s*(?:-\s*(\d+))?$/.exec(days);
          if (!range) return null;
          const min = parseInt(range[1], 10);
          const max = range[2] ? parseInt(range[2], 10) : min;
          return {
            label,
            prefixes: prefixes.split(/[\s,]+/).filter((prefix) => /^\d{1,5}$/.test(prefix)),
            min: Math.min(min, max),
            max: Math.max(min, max),
          };
        })
        .filter(Boolean);
    },

    // Longest matching ZIP prefix wins; unmatched ZIPs use the default range.
    matchZone(zip, zones, fallback) {
      let best = null;
      let bestLength = 0;
      zones.forEach((zone) => {
        zone.prefixes.forEach((prefix) => {
          if (zip.startsWith(prefix) && prefix.length > bestLength) {
            best = zone;
            bestLength = prefix.length;
          }
        });
      });
      return best || fallback;
    },

    isShippingDay(date, config, holidays) {
      const day = date.getUTCDay();
      if (!config.shipWeekends && (day === 0 || day === 6)) return false;
      return !holidays.has(DeliveryEstimateUtils.isoDate(date));
    },

    isDeliveryDay(date, config, holidays) {
      const day = date.getUTCDay();
      if (day === 0) return false;
      if (day === 6 && !config.saturdayDelivery) return false;
      return !holidays.has(DeliveryEstimateUtils.isoDate(date));
    },

    addDays(start, count, isValidDay) {
      let date = new Date(start.getTime());
      let remaining = count;
      while (remaining > 0) {
        date = new Date(date.getTime() + DAY_MS);
        if (isValidDay(date)) remaining -= 1;
      }
      return date;
    },

    calculate(config, range, now = Date.now()) {
      const { isShippingDay, isDeliveryDay, addDays, startOfDay, shopNow } = DeliveryEstimateUtils;
      const holidays = DeliveryEstimateUtils.parseHolidays(config.holidays);
      const shippingDay = (date) => isShippingDay(date, config, holidays);
      const deliveryDay = (date) => isDeliveryDay(date, config, holidays);

      const localNow = shopNow(config.shopOffset, now);
      const today = startOfDay(localNow);
      const beforeCutoff = localNow.getUTCHours() < config.cutoffHour;

      // The day the order is counted as received.
      let orderDay = today;
      if (!shippingDay(today) || !beforeCutoff) orderDay = addDays(today, 1, shippingDay);

      const shipDay = addDays(orderDay, config.processingDays, shippingDay);

      let cutoffRemaining = null;
      if (shippingDay(today) && beforeCutoff) {
        const cutoff = new Date(today.getTime() + config.cutoffHour * 60 * 60 * 1000);
        cutoffRemaining = cutoff.getTime() - localNow.getTime();
      }

      return {
        start: addDays(shipDay, range.min, deliveryDay),
        end: addDays(shipDay, range.max, deliveryDay),
        cutoffRemaining,
      };
    },
  };

  window.DeliveryEstimateUtils = DeliveryEstimateUtils;

  customElements.define(
    'delivery-estimate',
    class DeliveryEstimate extends HTMLElement {
      connectedCallback() {
        const configElement = this.querySelector('[data-delivery-config]');
        if (!configElement) return;

        this.config = JSON.parse(configElement.textContent);
        this.strings = this.config.strings;
        this.zones = DeliveryEstimateUtils.parseZones(this.config.zones);
        this.defaultRange = { min: this.config.transitMin, max: this.config.transitMax };
        this.formatter = this.createFormatter();

        this.result = this.querySelector('[data-delivery-result]');
        this.cutoff = this.querySelector('[data-delivery-cutoff]');
        this.location = this.querySelector('[data-delivery-location]');
        this.changeButton = this.querySelector('[data-delivery-change]');
        this.changeLabel = this.querySelector('[data-delivery-change-label]');
        this.form = this.querySelector('[data-delivery-form]');
        this.input = this.form.querySelector('input[name="zip"]');
        this.error = this.querySelector('[data-delivery-error]');

        this.onSubmit = this.onSubmit.bind(this);
        this.onChangeClick = this.onChangeClick.bind(this);
        this.form.addEventListener('submit', this.onSubmit);
        this.changeButton.addEventListener('click', this.onChangeClick);

        if (window.theme?.pubsub) {
          this.variantChangeUnsubscriber = theme.pubsub.subscribe(
            theme.pubsub.PUB_SUB_EVENTS.variantChange,
            this.onVariantChange.bind(this)
          );
        }

        this.zip = this.getStoredZip();
        this.render();
        this.cutoffTimer = setInterval(() => this.render(), 60 * 1000);
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        clearInterval(this.cutoffTimer);
        this.form?.removeEventListener('submit', this.onSubmit);
        this.changeButton?.removeEventListener('click', this.onChangeClick);
      }

      get sectionId() {
        return this.closest('product-info')?.sectionId || this.dataset.sectionId;
      }

      createFormatter() {
        const options = { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' };
        try {
          return new Intl.DateTimeFormat(this.config.locale, options);
        } catch (error) {
          return new Intl.DateTimeFormat(undefined, options);
        }
      }

      getStoredZip() {
        try {
          const zip = window.localStorage.getItem(ZIP_STORAGE_KEY);
          return /^\d{5}$/.test(zip || '') ? zip : null;
        } catch (error) {
          return null;
        }
      }

      storeZip(zip) {
        try {
          window.localStorage.setItem(ZIP_STORAGE_KEY, zip);
        } catch (error) {
          // Storage unavailable (private mode, blocked site data); keep the ZIP for this page only.
        }
      }

      onVariantChange(event) {
        if (event?.data?.sectionId !== this.sectionId) return;
        const variant = event.data.variant;
        // Unavailable option combinations publish no variant; keep the current state.
        if (!variant) return;
        this.toggleAttribute('hidden', variant.requires_shipping === false);
      }

      onChangeClick() {
        this.toggleForm(this.form.hasAttribute('hidden'));
      }

      toggleForm(open) {
        this.form.toggleAttribute('hidden', !open);
        this.changeButton.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          this.input.value = this.zip || '';
          this.input.focus();
        }
      }

      onSubmit(event) {
        event.preventDefault();
        const zip = this.input.value.trim();

        if (!/^\d{5}$/.test(zip)) {
          this.error.removeAttribute('hidden');
          this.input.setAttribute('aria-invalid', 'true');
          return;
        }

        this.error.setAttribute('hidden', '');
        this.input.removeAttribute('aria-invalid');
        this.zip = zip;
        this.storeZip(zip);
        this.toggleForm(false);
        this.render();
        this.changeButton.focus();
      }

      render() {
        const knownDestination = this.zip || this.config.country === 'US';

        if (!knownDestination) {
          this.result.textContent = this.strings.usOnly;
          this.cutoff.setAttribute('hidden', '');
          this.location.setAttribute('hidden', '');
          this.changeButton.setAttribute('hidden', '');
          this.form.removeAttribute('hidden');
          this.changeButton.setAttribute('aria-expanded', 'true');
          return;
        }

        const range = this.zip
          ? DeliveryEstimateUtils.matchZone(this.zip, this.zones, this.defaultRange)
          : this.defaultRange;
        const estimate = DeliveryEstimateUtils.calculate(this.config, range);
        const start = this.formatter.format(estimate.start);
        const end = this.formatter.format(estimate.end);

        this.result.textContent =
          start === end
            ? this.strings.arrives.replace('[date]', start)
            : this.strings.arrivesRange.replace('[start]', start).replace('[end]', end);

        if (this.config.showCutoff && estimate.cutoffRemaining !== null) {
          this.cutoff.textContent = this.strings.orderWithin.replace(
            '[time]',
            this.formatDuration(estimate.cutoffRemaining)
          );
          this.cutoff.removeAttribute('hidden');
        } else {
          this.cutoff.setAttribute('hidden', '');
        }

        this.location.textContent = this.zip
          ? this.strings.deliveringTo.replace('[zip]', this.zip)
          : this.strings.enterZipHint;
        this.changeLabel.textContent = this.zip ? this.strings.change : this.strings.enterZip;
        this.location.removeAttribute('hidden');
        this.changeButton.removeAttribute('hidden');
      }

      formatDuration(milliseconds) {
        const totalMinutes = Math.max(1, Math.ceil(milliseconds / 60000));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (hours === 0) return this.strings.minutes.replace('[minutes]', minutes);
        return this.strings.hoursMinutes.replace('[hours]', hours).replace('[minutes]', minutes);
      }
    }
  );
}
